import cx from 'classnames'
import { useState, useEffect } from 'react'
import { type Handlers, type KeyMap, HotKeys } from 'react-keyboard'
import Popup from 'reactjs-popup'

import { type SanityFrameBuilderTextureImage } from '../../data/sanity/queries/types/texture'

import Arrows from './arrows'
import { type ImageSize } from './image-slide'
import ImageSlides from './image-slides'
import Thumbnails from './thumbnails'

interface GalleryProps {
  images: SanityFrameBuilderTextureImage[]
  className?: string
}

const Gallery = ({ images, className }: GalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [currentImageSize, setCurrentImageSize] = useState<ImageSize>({})

  // Reset index when images change
  useEffect(() => {
    setSelectedIndex(0)
  }, [images])

  const showPreviousImage = () => {
    setSelectedIndex((index) => {
      return index > 0 ? index - 1 : index
    })
  }
  const showNextImage = () => {
    setSelectedIndex((index) => {
      return index < images.length - 1 ? index + 1 : index
    })
  }

  const keyMap: KeyMap = {
    left: 'left',
    right: 'right',
  }
  const handlers: Handlers = {
    left: showPreviousImage,
    right: showNextImage,
  }

  return (
    <div className={cx('w-full h-[64px] sm:h-[84px] md:h-[120px]', className)}>
      <Thumbnails
        images={images}
        setIsModalOpen={setIsModalOpen}
        setSelectedIndex={setSelectedIndex}
      />

      <Popup
        modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        lockScroll
        closeOnDocumentClick
        closeOnEscape
        repositionOnResize
        overlayStyle={{
          backgroundColor: 'rgba(0,0,0,0.25)',
          cursor: 'zoom-out',
        }}
        contentStyle={{
          width: '100%',
          height: '100%',
          padding: '0',
          border: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <HotKeys keyMap={keyMap} handlers={handlers}>
          <Arrows
            imageSize={currentImageSize}
            leftDisabled={selectedIndex <= 0}
            rightDisabled={selectedIndex >= images.length - 1}
            onLeftClick={showPreviousImage}
            onRightClick={showNextImage}
          />

          <ImageSlides
            images={images}
            selectedIndex={selectedIndex}
            onSwipedLeft={showNextImage}
            onSwipedRight={showPreviousImage}
            setIsModalOpen={setIsModalOpen}
            setCurrentImageSize={setCurrentImageSize}
          />
        </HotKeys>
      </Popup>
    </div>
  )
}

export default Gallery
